import { camelizeKeys } from 'humps';
import get from 'lodash/get';

import warehouse from 'services/warehouse';
import modsyMixpanel from 'services/analytics/modsy_mixpanel';
import analytics from 'services/analytics';
import { bugsnagError } from 'services/bugsnag';
import { isAdminUser } from 'redux/User';

import {
  dataURItoBlob,
  asyncForEach,
  convertUnits,
  isDataURL
} from 'utils/utils';

import {
  UPDATE_PPO_DATA,
  UPDATE_PPO_DATA_LOADING,
  UPDATE_PPO_STATE,
  UPDATE_PPO_STATE_LOADING,
  UPDATE_STYLEBOARD_INFO,
  UPDATE_STYLEBOARD_INFO_LOADING,
  SET_FETCH_STYLEBOARDINFO,
  UPDATE_STYLEBOARD,
  UPDATE_STYLEBOARD_LOADING,
  SET_FETCH_SPACEDATA,
  SET_PHOTO_SUBMIT_SUCCESS,
  SET_PHOTO_WARNING,
  SET_PHOTOS_UPLOADING,
  SET_PHOTOS_UPLOAD_TOTAL,
  SET_PHOTOS_UPLOAD_CURRENT,
  UPDATE_PHOTOS_AND_MEASUREMENTS,
  UPDATE_PHOTOS_AND_MEASUREMENTS_LOADING,
  UPDATE_ABOUT_YOU,
  UPDATE_ABOUT_YOU_LOADING,
  UPDATE_ABOUT_YOU_SUBMITTING,
  UPDATE_PRODUCT_CATEGORIES,
  UPDATE_PRODUCT_CATEGORIES_LOADING,
  SET_STYLEBOARD_SUBMIT_SUCCESS,
  SET_STYLEBOARD_SUBMIT_ERROR,
  SET_STYLEBOARD_SUBMIT_LOADING,
  UPDATE_SPACE_LIST,
  UPDATE_SPACE_LIST_LOADING,
  SET_SPACELIST_PAGE,
  SET_SPACE_USER_ID,
  SET_SPACE_USER_ID_LOADING,
  SET_STYLE_MATCH_QUIZ_COMPLETE,
  SET_STYLE_MATCH_QUIZ_COMPLETE_LOADING,
  UPDATE_STYLEBOARD_V2,
  SET_LEGACY_QUIZ_RESULT,
  SET_LEGACY_QUIZ_RESULT_LOADING
} from './action-constants';

import {
  getIsStyleboardInfoLoading,
  getIsSpaceLoading,
  getSpace,
  getArePhotosAndMeasurementsLoading,
  getIsAboutYouDataLoading,
  getIsAboutYouDataSubmitting,
  getIsProductCategoriesDataLoading,
  getIsSpaceListLoading,
  getIsSpaceUserIdLoading,
  getSpaceUserId,
  getIsStyleMatchQuizCompleteLoading,
  getIsLegacyQuizResultLoading
} from './selectors';

// getting 100 spaces to match the requests in account and studio
const PPO_SPACE_PAGE_SIZE = 100;

export const requestStyleboardInfo = (styleboardId) => async (dispatch, getState) => {
  if (!styleboardId || getIsStyleboardInfoLoading(getState())) { return; }

  dispatch({
    type: UPDATE_STYLEBOARD_INFO_LOADING,
    payload: true
  });

  const [mobileInfo, responseData] = await Promise.all([
    warehouse.getMobilePPOInfo(styleboardId),
    warehouse.getStyleboardInfo(styleboardId)
  ]);

  const styleboardInfo = {
    ...responseData.results[0],
    roomCaptureCompleted: get(mobileInfo, 'dashboard.room_capture.completed', false)
  };

  dispatch({
    type: UPDATE_STYLEBOARD_INFO,
    payload: camelizeKeys(styleboardInfo)
  });
  dispatch({
    type: UPDATE_STYLEBOARD_INFO_LOADING,
    payload: false
  });
  dispatch(setShouldFetchStyleboardInfo(false));
};

export const updateStyleboardInfo = (styleboardInfo, allowEmpty = false) => (dispatch) => {
  return warehouse.patchStyleboardInfo({ styleboardInfo, allowEmpty }).then(responseData => {
    const styleboardInfo = responseData;
    dispatch({
      type: UPDATE_STYLEBOARD_INFO,
      payload: camelizeKeys(styleboardInfo)
    });
    dispatch(setShouldFetchStyleboardInfo(true));
    return styleboardInfo;
  });
};

export const setShouldFetchStyleboardInfo = (shouldFetch) => ({
  type: SET_FETCH_STYLEBOARDINFO,
  payload: !!shouldFetch
});

export const updateSpace = (styleboardInfo) => async (dispatch) => {
  const obj = {
    name: styleboardInfo.name,
    spaceType: styleboardInfo.spaceType
  };

  const spaceId = styleboardInfo.styleboard;

  dispatch({
    type: UPDATE_STYLEBOARD_LOADING,
    payload: true
  });

  await warehouse.patchStyleboard(spaceId, obj).then(async response => {
    const styleboardResp = response;
    // this response is different than the requestSpaceData one so don't update the styleboard
    // directly, instead request it again

    // we need to set loading to false before we
    // request again or it won't make the request
    dispatch({
      type: UPDATE_STYLEBOARD_LOADING,
      payload: false
    });

    dispatch(setShouldFetchSpaceData(true));
    await dispatch(requestSpaceData(spaceId));

    return styleboardResp;
  });
};

export const requestPPOState = (spaceId) => (dispatch, getState) => {
  dispatch({
    type: UPDATE_PPO_STATE_LOADING,
    payload: true
  });

  return warehouse.getPPOState(spaceId).then(responseData => {
    const ppoState = responseData;
    dispatch({
      type: UPDATE_PPO_STATE,
      payload: ppoState
    });

    dispatch({
      type: UPDATE_PPO_STATE_LOADING,
      payload: false
    });
    return ppoState;
  });
};

export const requestPPOData = (spaceId) => (dispatch, getState) => {
  dispatch({
    type: UPDATE_PPO_DATA_LOADING,
    payload: true
  });

  return warehouse.getPPOData(spaceId).then(responseData => {
    const ppoData = responseData;
    dispatch({
      type: UPDATE_PPO_DATA,
      payload: ppoData
    });

    dispatch({
      type: UPDATE_PPO_DATA_LOADING,
      payload: false
    });
    return ppoData;
  });
};

export const updateSpaceV2 = (spaceV2) => ({
  type: UPDATE_STYLEBOARD_V2,
  payload: spaceV2
});

export const requestSpaceData = (spaceId) => (dispatch, getState) => {
  if (!spaceId || getIsSpaceLoading(getState())) { return; }

  dispatch({
    type: UPDATE_STYLEBOARD_LOADING,
    payload: true
  });

  return warehouse.getSpaceData(spaceId).then(responseData => {
    const spaceData = responseData;
    dispatch({
      type: UPDATE_STYLEBOARD,
      payload: camelizeKeys(spaceData)
    });
    dispatch({
      type: UPDATE_STYLEBOARD_LOADING,
      payload: false
    });
    dispatch(setShouldFetchSpaceData(false));
    return spaceData;
  });
};

export const setShouldFetchSpaceData = (shouldFetch) => ({
  type: SET_FETCH_SPACEDATA,
  payload: !!shouldFetch
});

// method used in the Experimental PPO
export const submitSpacePhotosWithProgress = (spaceId, photosWithIndexes,
  uploadPhotoProgress = () => { },
  updatePhotoId = () => { }) => (dispatch, getState) => {
  if (photosWithIndexes.length === 0) {
    bugsnagError(
      'No photos have been uploaded',
      { spaceId }
    );
    return;
  }
  modsyMixpanel.trackUploadPhotos('begin', spaceId);
  modsyMixpanel.trackUploadPhotos('ready', spaceId, photosWithIndexes.length);
  let iter = photosWithIndexes[Symbol.iterator]();
  submitSpacePhotosWithProgressHelper(iter, spaceId, uploadPhotoProgress, updatePhotoId, dispatch);
};
const submitSpacePhotosWithProgressHelper = (iter, spaceId, uploadPhotoProgress, updatePhotoId, dispatch) => {
  let nextFileIter = iter.next();
  let photoData = nextFileIter.value;
  let done = nextFileIter.done;
  if (done) {
    return;
  }
  const photoFile = photoData.photoFile;
  const photoIndex = photoData.photoIndex;

  const reader = new window.FileReader();
  reader.onload = () => {
    const photoUrl = reader.result;
    const formPhoto = new window.FormData();
    formPhoto.append(`${0}`, dataURItoBlob(photoUrl));

    const uploadProgressCallback = (progressEvent) => {
      var percentCompleted = progressEvent.loaded / progressEvent.total;
      uploadPhotoProgress(photoIndex, percentCompleted);
    };
    const updatePhotoIdCallback = (id) => updatePhotoId(photoIndex, id);
    // Mobile performance bug with proper upload process tracking.
    var fakeProgress = 0.4 * Math.random();
    uploadPhotoProgress(photoIndex, fakeProgress);
    warehouse.submitSpacePhoto(spaceId, formPhoto, uploadProgressCallback, updatePhotoIdCallback).then(responseData => {
      fakeProgress += 0.4 * Math.random();
      uploadPhotoProgress(photoIndex, fakeProgress);
      setTimeout(() => { uploadPhotoProgress(photoIndex, 1); }, Math.random() * 1000);

      if (responseData.success) {
        dispatch(analytics.ppoPhotosSubmitted({ studioId: spaceId, numberOfPhotos: 1 }));
        modsyMixpanel.trackUploadPhotos('success', spaceId, 1);
      } else {
        modsyMixpanel.trackUploadPhotos('failure', spaceId, 1, responseData.error || 'Some error happened');
      }
    });
    submitSpacePhotosWithProgressHelper(iter, spaceId, uploadPhotoProgress, updatePhotoId, dispatch);
  };
  reader.onerror = function (error) {
    console.log(error);
  };
  reader.readAsDataURL(photoFile);
};

export const submitSpacePhotos = (spaceId, photos, callback = () => {}) => async (dispatch, getState) => {
  if (photos.length === 0) {
    bugsnagError(
      'No photos have been uploaded',
      { spaceId }
    );
    return;
  }

  const numberOfPhotos = photos.length;

  modsyMixpanel.trackUploadPhotos('begin', spaceId);

  // Build up an *array* of forms, each with one image each, then process them one by one.
  // This might decrease overall throughput/speed, but it's safer with more numerous,
  // but shorter duration api requests.
  const forms = photos.map((photo, index) => {
    const form = new window.FormData();
    form.append(`${index}`, dataURItoBlob(photo.url));
    return form;
  });

  modsyMixpanel.trackUploadPhotos('ready', spaceId, forms.length);
  let success = true;
  let error = '';

  dispatch({
    type: SET_PHOTOS_UPLOADING,
    payload: true
  });

  let totalUploaded = 0;
  dispatch({
    type: SET_PHOTOS_UPLOAD_TOTAL,
    payload: photos.length
  });

  dispatch({
    type: SET_PHOTOS_UPLOAD_CURRENT,
    payload: totalUploaded
  });

  // wait for all photo uploads to finish
  await asyncForEach(forms, async (photoData) => {
    await warehouse.submitSpacePhotos(spaceId, photoData).then(responseData => {
      success = success && responseData.success;
      if (!responseData.success) { error = responseData.error; }
      if (responseData.success) {
        dispatch({
          type: SET_PHOTOS_UPLOAD_CURRENT,
          payload: ++totalUploaded
        });
      }
    });
  });

  dispatch({
    type: SET_PHOTOS_UPLOADING,
    payload: false
  });

  dispatch({
    type: SET_PHOTO_SUBMIT_SUCCESS,
    payload: {
      success,
      error
    }
  });

  if (success) {
    dispatch(analytics.ppoPhotosSubmitted({ studioId: spaceId, numberOfPhotos }));
    modsyMixpanel.trackUploadPhotos('success', spaceId, numberOfPhotos);
  } else {
    modsyMixpanel.trackUploadPhotos('failure', spaceId, numberOfPhotos, error || 'Some error happened');
  }

  if (callback && typeof (callback) === 'function') { callback(success); }
};

export const warningSpacePhotos = (warning) => ({
  type: SET_PHOTO_WARNING,
  payload: {
    warning
  }
});

export const deletePhoto = (photoId) => (dispatch, getState) => {
  const spaceId = get(getSpace(getState()), 'id');
  warehouse.removePhoto({ photoId, spaceId })
    .catch(error => {
      // We do not want to interrupt user flow here so just
      // log out error and continue. In the future we may
      // want to display some kind of error message on the
      // image.
      console.log(error);
      bugsnagError(
        'Error deleting photo',
        { error }
      );
    });
};

export const requestPhotosAndMeasurementData = (spaceId) => (dispatch, getState) => {
  if (!spaceId || getArePhotosAndMeasurementsLoading(getState())) { return; }

  dispatch({
    type: UPDATE_PHOTOS_AND_MEASUREMENTS_LOADING,
    payload: true
  });

  return warehouse.getPhotosAndMeasurementData(spaceId).then(responseData => {
    const photosAndMeasurementsData = responseData;
    dispatch({
      type: UPDATE_PHOTOS_AND_MEASUREMENTS,
      payload: camelizeKeys(photosAndMeasurementsData)
    });
    dispatch({
      type: UPDATE_PHOTOS_AND_MEASUREMENTS_LOADING,
      payload: false
    });
  });
};

export const submitShapeData = ({ key, spaceId } = {}) => async (dispatch, getState) => {
  const data = { questions: [] };
  data.questions.push({
    name: 'room-details.space-shape.spaceshape_questions',
    value: {
      choices: [
        {
          name: key,
          selected: true
        }
      ]
    }
  });
  await warehouse.patchPPOData(data, spaceId);
};

export const submitMeasurements = (spaceId, data) => async (dispatch, getState) => {
  const {
    doorHeight,
    doorHeight2,
    width,
    width2,
    length,
    length2,
    measurementUnit
  } = data;

  const totalWidth = convertUnits(measurementUnit, width, width2);
  const totalLength = convertUnits(measurementUnit, length, length2);
  const convertedDoorHeight = convertUnits(
    measurementUnit,
    doorHeight,
    doorHeight2
  );

  const measurementsData = {
    door_height: parseFloat(convertedDoorHeight).toFixed(2),
    length: parseFloat(totalLength).toFixed(2),
    width: parseFloat(totalWidth).toFixed(2),
    measurement_unit: measurementUnit
  };

  await warehouse.submitMeasurementData(spaceId, measurementsData);
  dispatch(setShouldFetchStyleboardInfo(true));
};

export const submitFloorPlan = (spaceId, data) => async (dispatch, getState) => {
  const floorPlan = get(data, 'url');
  const form = new window.FormData();

  if (floorPlan && isDataURL(floorPlan)) {
    form.append('photo', dataURItoBlob(floorPlan));
    form.append('pos', 7);

    await warehouse.submitFloorPlan(spaceId, form);
  }

  dispatch(setShouldFetchStyleboardInfo(true));
  dispatch(setShouldFetchSpaceData(true));
};

// method used in the Experimental PPO
export const submitFloorPlanWithProgress = (spaceId, photoFile,
  uploadPhotoProgress = () => { },
  updatePhotoId = () => { }) => (dispatch) => {
  const reader = new window.FileReader();
  reader.onload = () => {
    const photoUrl = reader.result;
    const formPhoto = new window.FormData();
    formPhoto.append('photo', dataURItoBlob(photoUrl));
    formPhoto.append('pos', 7);

    const uploadProgressCallback = (progressEvent) => {
      var percentCompleted = progressEvent.loaded / progressEvent.total;
      uploadPhotoProgress(percentCompleted);
    };
    // Mobile performance bug with proper upload process tracking.
    var fakeProgress = 0.2 * Math.random();
    uploadPhotoProgress(fakeProgress);
    setTimeout(() => {
      fakeProgress += 0.4 * Math.random();
      uploadPhotoProgress(fakeProgress);
    }, Math.random() * 2000);
    warehouse.submitFloorPlanExperimental(spaceId, formPhoto, uploadProgressCallback, updatePhotoId).then(() => {
      fakeProgress += 0.4 * Math.random();
      uploadPhotoProgress(fakeProgress);
      setTimeout(() => { uploadPhotoProgress(1); }, Math.random() * 1000);

      dispatch(setShouldFetchStyleboardInfo(true));
      dispatch(setShouldFetchSpaceData(true));
    });
    reader.onerror = function (error) {
      console.log(error);
    };
  };
  reader.readAsDataURL(photoFile);
};

export const setStyleboardSubmitError = (errorMessage) => ({
  type: SET_STYLEBOARD_SUBMIT_ERROR,
  payload: errorMessage
});

export const setStyleboardSubmitLoading = (isLoading) => ({
  type: SET_STYLEBOARD_SUBMIT_LOADING,
  payload: isLoading
});

export const setStyleboardSubmitSuccess = (isSuccessful) => ({
  type: SET_STYLEBOARD_SUBMIT_SUCCESS,
  payload: isSuccessful
});

export const submitSpace = (spaceId) => async (dispatch, getState) => {
  if (!spaceId) {
    const errorMessage = 'No space id has been supplied';
    dispatch(setStyleboardSubmitError(errorMessage));
    bugsnagError(new Error(errorMessage), { spaceId });
    return;
  }

  dispatch(setStyleboardSubmitLoading(true));

  try {
    await warehouse.submitSpace(spaceId);
    dispatch(setShouldFetchStyleboardInfo(true));
    dispatch(setShouldFetchSpaceData(true));
    dispatch(setStyleboardSubmitSuccess(true));
  } catch (error) {
    const { message: errorMessage } = error;
    dispatch(setStyleboardSubmitError(errorMessage));
    bugsnagError(error, { spaceId });
  }
};

export const requestAboutYouData = (userId) => async (dispatch, getState) => {
  const state = getState();
  if (
    !userId ||
    getIsAboutYouDataLoading(state) ||
    getIsAboutYouDataSubmitting(state)
  ) {
    return;
  }

  dispatch({
    type: UPDATE_ABOUT_YOU_LOADING,
    payload: true
  });

  const isUserAdmin = isAdminUser(state);

  // this is the user id of the user we want the About You data for
  // either the signed-in user if this is their space
  // or the space's user id if the signed-in user is an admin
  let controlUserId = userId;
  if (userId && isUserAdmin) {
    const spaceUserId = getSpaceUserId(state);
    if (spaceUserId === userId || (isUserAdmin && spaceUserId)) {
      controlUserId = spaceUserId;
    }
  }

  return warehouse.getAboutYou(controlUserId).then(responseData => {
    const aboutYouData = responseData;

    dispatch({
      type: UPDATE_ABOUT_YOU,
      payload: camelizeKeys(aboutYouData)
    });

    dispatch({
      type: UPDATE_ABOUT_YOU_LOADING,
      payload: false
    });
  });
};

export const submitAboutYouData = ({
  aboutYouText,
  firstTime,
  phoneNumber,
  phoneTextingEnabled,
  userId,
  whoLivesHereAdditional,
  whoLivesHereOptions
}) => async (dispatch) => {
  dispatch({
    type: UPDATE_ABOUT_YOU_SUBMITTING,
    payload: true
  });
  try {
    const apiFcn = firstTime ? warehouse.submitAboutYou : warehouse.updateAboutYou;
    await apiFcn({
      aboutYouText,
      phoneNumber,
      phoneTextingEnabled,
      userId,
      whoLivesHereAdditional,
      whoLivesHereOptions
    });

    // Fire fetch request after POST has completed
    // to ensure we always have the latest data in state.
    const aboutYouData = await warehouse.getAboutYou(userId);
    dispatch({
      type: UPDATE_ABOUT_YOU,
      payload: camelizeKeys(aboutYouData)
    });
  } catch (error) {
    bugsnagError(error, { userId });
  } finally {
    dispatch({
      type: UPDATE_ABOUT_YOU_SUBMITTING,
      payload: false
    });
  }
};

export const requestProductCategoriesData = (spaceId) => async (dispatch, getState) => {
  if (getIsProductCategoriesDataLoading(getState())) { return; }

  dispatch({
    type: UPDATE_PRODUCT_CATEGORIES_LOADING,
    payload: true
  });

  return warehouse.getPPOProductCategories(spaceId).then(responseData => {
    const productCategoriesData = responseData;

    dispatch({
      type: UPDATE_PRODUCT_CATEGORIES,
      payload: camelizeKeys(productCategoriesData)
    });

    dispatch({
      type: UPDATE_PRODUCT_CATEGORIES_LOADING,
      payload: false
    });
  });
};

// maps the spaceList array to an object where the keys are the space ids
const mapSpacesWithIdKeys = (spaceList) => {
  let spaceObj = {};

  for (const space in spaceList) {
    spaceObj[spaceList[space].id] = spaceList[space];
  }

  return spaceObj;
};

export const requestSpaceList = (userId, page, pageSize = PPO_SPACE_PAGE_SIZE) => async (dispatch, getState) => {
  if (getIsSpaceListLoading(getState())) { return; }

  dispatch({
    type: UPDATE_SPACE_LIST_LOADING,
    payload: true
  });

  return warehouse.getStudioSpaceListFor({page, userId, pageSize}).then(responseData => {
    const spaceList = mapSpacesWithIdKeys(responseData.results);

    dispatch({
      type: UPDATE_SPACE_LIST,
      payload: {spaceList, spaceListPage: page}
    });

    dispatch({
      type: UPDATE_SPACE_LIST_LOADING,
      payload: false
    });
  });
};

export const resetSpaceListPage = (shouldFetch) => ({
  type: SET_SPACELIST_PAGE,
  payload: 1
});

export const requestSpaceUserId = (spaceId, isAdminUser) => async (dispatch, getState) => {
  if (getIsSpaceUserIdLoading(getState())) { return; }
  dispatch({
    type: SET_SPACE_USER_ID_LOADING,
    payload: true
  });
  return warehouse.getSpaceUser({spaceId, isAdminUser}).then(spaceUserId => {
    dispatch({
      type: SET_SPACE_USER_ID,
      payload: spaceUserId
    });
    dispatch({
      type: SET_SPACE_USER_ID_LOADING,
      payload: false
    });
  });
};

export const requestStyleMatchQuizCompleted = (spaceId) => async (dispatch, getState) => {
  if (getIsStyleMatchQuizCompleteLoading(getState())) { return; }
  dispatch({
    type: SET_STYLE_MATCH_QUIZ_COMPLETE_LOADING,
    payload: true
  });

  return warehouse.getStyleQuizComplete({ spaceId }).then(styleMatchQuizComplete => {
    dispatch(setStyleMatchQuizCompleted(styleMatchQuizComplete));
    dispatch({
      type: SET_STYLE_MATCH_QUIZ_COMPLETE_LOADING,
      payload: false
    });
  });
};

export const setStyleMatchQuizCompleted = (isCompleted) => ({
  type: SET_STYLE_MATCH_QUIZ_COMPLETE,
  payload: isCompleted
});

export const requestLegacyQuizResult = ({spaceId, userId}) => async (dispatch, getState) => {
  if (getIsLegacyQuizResultLoading(getState())) { return; }
  dispatch({
    type: SET_LEGACY_QUIZ_RESULT_LOADING,
    payload: true
  });

  return warehouse.getLegacyStyleQuizResult({ spaceId, userId }).then(legacyQuizResponse => {
    dispatch(setLegacyQuizResult(legacyQuizResponse && legacyQuizResponse.result));
    dispatch({
      type: SET_LEGACY_QUIZ_RESULT_LOADING,
      payload: false
    });
  });
};

export const setLegacyQuizResult = (result) => {
  return ({
    type: SET_LEGACY_QUIZ_RESULT,
    payload: result
  });
};
